import dayjsTZ from "../../../utils/dayjs";
import { groupSingleShift } from "../../Pagat/PaySub/employeeCalc/calculcate";

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

const empName = (emp = {}, row = {}) =>
  `${emp.employeeFirstName || row.employeeFirstName || ""} ${
    emp.employeeLastName || row.employeeLastName || ""
  }`.trim() ||
  row.employeeFullName ||
  "—";

export function isActiveEmployee(emp = {}) {
  return String(emp.employeeStatus || "")
    .toLowerCase()
    .trim() === "aktiv";
}

/**
 * Slice a closed clocking into hour buckets via groupSingleShift.
 */
export function shiftHours(clocking) {
  if (!clocking?.clockInDate || !clocking?.clockOutDate) {
    return { before19: 0, between19and22: 0, after22: 0, total: 0 };
  }
  const {
    NORMAL_HOURS = {},
    LATE_HOURS = {},
    EXTRA_HOURS = {},
  } = groupSingleShift(clocking) || {};
  const before19 = Number(NORMAL_HOURS?.amount) || 0;
  const between19and22 = Number(LATE_HOURS?.amount) || 0;
  const after22 = Number(EXTRA_HOURS?.amount) || 0;
  return {
    before19,
    between19and22,
    after22,
    total: before19 + between19and22 + after22,
  };
}

export function filterClockings(
  clockings,
  { department, team, employeeId, activeOnly = true },
) {
  return (clockings || []).filter((c) => {
    if (employeeId && c.employeeId !== employeeId) return false;
    const emp = c.employeeDetails || {};
    if (activeOnly && !isActiveEmployee(emp)) return false;
    if (
      department &&
      (emp.employeeDepartmentName || c.employeeDepartmentName) !== department
    ) {
      return false;
    }
    if (team && (emp.employeeTeam || c.employeeTeam) !== team) return false;
    return true;
  });
}

export function buildKpis(clockings, leaveDaysUsed, lateMinutes) {
  let totalHours = 0;
  let otHours = 0;
  for (const c of clockings) {
    const h = shiftHours(c);
    totalHours += h.total;
    otHours += h.between19and22 + h.after22;
  }
  const minutes = Number(lateMinutes) || 0;
  return {
    totalHours: round2(totalHours),
    otHours: round2(otHours),
    lateMinutes: Math.round(minutes),
    lateHours: round2(minutes / 60),
    leaveDaysUsed: leaveDaysUsed || 0,
  };
}

export function hoursMix(clockings) {
  let before19 = 0;
  let between19and22 = 0;
  let after22 = 0;
  for (const c of clockings) {
    const h = shiftHours(c);
    before19 += h.before19;
    between19and22 += h.between19and22;
    after22 += h.after22;
  }
  return [
    { name: "Para 19:00", value: round2(before19), fill: "#1da193" },
    { name: "19:00 - 22:00", value: round2(between19and22), fill: "#FCC94A" },
    { name: "Pas 22:00", value: round2(after22), fill: "#ea3943" },
  ];
}

/** Group total hours by day / week / month. */
export function hoursOverTime(clockings, { grain = "month" } = {}) {
  const map = new Map();
  for (const c of clockings) {
    if (!c?.clockInDate || !c?.clockOutDate) continue;
    const d = dayjsTZ(c.clockInDate);
    let key;
    let sortKey;
    if (grain === "day") {
      key = d.format("DD/MM");
      sortKey = d.startOf("day").valueOf();
    } else if (grain === "week") {
      key = d.startOf("week").format("DD/MM");
      sortKey = d.startOf("week").valueOf();
    } else {
      key = d.format("MMM YY");
      sortKey = d.startOf("month").valueOf();
    }
    const h = shiftHours(c);
    if (!map.has(key)) {
      map.set(key, {
        name: key,
        sortKey,
        before19: 0,
        between19and22: 0,
        after22: 0,
        total: 0,
      });
    }
    const row = map.get(key);
    row.before19 += h.before19;
    row.between19and22 += h.between19and22;
    row.after22 += h.after22;
    row.total += h.total;
  }
  return [...map.values()]
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((r) => ({
      name: r.name,
      before19: round2(r.before19),
      between19and22: round2(r.between19and22),
      after22: round2(r.after22),
      total: round2(r.total),
    }));
}

function groupByField(clockings, field) {
  const map = new Map();
  for (const c of clockings) {
    if (!c?.clockOutDate) continue;
    const emp = c.employeeDetails || {};
    const name = emp[field] || c[field] || "—";
    const h = shiftHours(c);
    if (!map.has(name)) {
      map.set(name, { name, total: 0, ot: 0 });
    }
    const row = map.get(name);
    row.total += h.total;
    row.ot += h.between19and22 + h.after22;
  }
  return [...map.values()]
    .map((r) => ({
      name: r.name,
      total: round2(r.total),
      ot: round2(r.ot),
    }))
    .sort((a, b) => b.total - a.total);
}

export function hoursByDepartment(clockings) {
  return groupByField(clockings, "employeeDepartmentName");
}

export function hoursByTeam(clockings) {
  return groupByField(clockings, "employeeTeam");
}

export function topByHours(clockings, limit = 10) {
  const map = new Map();
  for (const c of clockings) {
    if (!c?.clockOutDate || !c?.employeeId) continue;
    const emp = c.employeeDetails || {};
    const h = shiftHours(c);
    if (!map.has(c.employeeId)) {
      map.set(c.employeeId, {
        name: empName(emp, c),
        total: 0,
        ot: 0,
      });
    }
    const row = map.get(c.employeeId);
    row.total += h.total;
    row.ot += h.between19and22 + h.after22;
  }
  return [...map.values()]
    .map((r) => ({
      name: r.name,
      total: round2(r.total),
      ot: round2(r.ot),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

export function topByOvertime(clockings, limit = 10) {
  return topByHours(clockings, 50)
    .filter((r) => r.ot > 0)
    .sort((a, b) => b.ot - a.ot)
    .slice(0, limit)
    .map((r) => ({ name: r.name, value: r.ot }));
}

/**
 * Late minutes for first clock-in of the day vs planned shift start.
 * Returns minutes late (0 if on time / early).
 */
export function lateMinutesForClocking(clocking, shifts = []) {
  if (!clocking?.clockInDate) return 0;
  const shiftById = new Map((shifts || []).map((s) => [s.shiftId, s]));
  const emp = clocking.employeeDetails || {};
  const shiftIds = emp.employeeShiftId || [];
  const ids = Array.isArray(shiftIds) ? shiftIds : [shiftIds].filter(Boolean);
  const shift = shiftById.get(ids[0]);
  const startStr =
    shift?.shiftStartTime || shift?.startTime || shift?.shiftStart || "09:00";
  const [hh, mm] = String(startStr).split(":").map(Number);
  const planned = dayjsTZ(clocking.clockInDate)
    .hour(hh || 9)
    .minute(mm || 0)
    .second(0);
  const actual = dayjsTZ(clocking.clockInDate);
  if (!actual.isAfter(planned.add(1, "minute"))) return 0;
  return Math.max(0, actual.diff(planned, "minute"));
}

/**
 * Top employees by total late minutes (first clock-in per day vs shift start).
 * value = minutes; hours = minutes/60 for display.
 */
export function latenessLeaders(clockings, shifts = [], limit = 10) {
  const byEmpDay = new Map();

  for (const c of clockings) {
    if (!c?.clockInDate || !c?.employeeId) continue;
    const dayKey = `${c.employeeId}|${dayjsTZ(c.clockInDate).format(
      "YYYY-MM-DD",
    )}`;
    const prev = byEmpDay.get(dayKey);
    if (!prev || c.clockInDate < prev.clockInDate) {
      byEmpDay.set(dayKey, c);
    }
  }

  const lateByEmp = new Map();
  for (const c of byEmpDay.values()) {
    const minutes = lateMinutesForClocking(c, shifts);
    if (minutes <= 0) continue;

    const emp = c.employeeDetails || {};
    const id = c.employeeId;
    if (!lateByEmp.has(id)) {
      lateByEmp.set(id, { name: empName(emp, c), value: 0 });
    }
    lateByEmp.get(id).value += minutes;
  }

  return [...lateByEmp.values()]
    .map((r) => ({
      name: r.name,
      value: Math.round(r.value),
      hours: round2(r.value / 60),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

export function totalLateMinutes(clockings, shifts = []) {
  return latenessLeaders(clockings, shifts, 9999).reduce(
    (s, r) => s + r.value,
    0,
  );
}

export function leaveByType(
  requests,
  { startTs, endTs, employeeId, department, team, employees, activeOnly = true },
) {
  const empBySub = new Map((employees || []).map((e) => [e.userSub, e]));
  const map = new Map();
  for (const req of requests || []) {
    if (req?.requestStatus !== "Aprovuar") continue;
    const emp = req.employeeDetails || empBySub.get(req.userSub) || {};
    if (activeOnly && !isActiveEmployee(emp)) continue;
    if (employeeId && emp.employeeId !== employeeId) continue;
    if (department && emp.employeeDepartmentName !== department) continue;
    if (team && emp.employeeTeam !== team) continue;
    const period = Array.isArray(req.requestPeriod) ? req.requestPeriod : [];
    const days = period.filter((p) => {
      const t = dayjsTZ(p).valueOf();
      return t >= startTs && t <= endTs;
    }).length;
    if (!days) continue;
    const type = req.requestType || "Tjetër";
    map.set(type, (map.get(type) || 0) + days);
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function leaveTopUsers(
  requests,
  { startTs, endTs, employeeId, department, team, employees, activeOnly = true },
  limit = 10,
) {
  const empBySub = new Map((employees || []).map((e) => [e.userSub, e]));
  const map = new Map();
  for (const req of requests || []) {
    if (req?.requestStatus !== "Aprovuar") continue;
    const emp = req.employeeDetails || empBySub.get(req.userSub) || {};
    if (activeOnly && !isActiveEmployee(emp)) continue;
    if (employeeId && emp.employeeId !== employeeId) continue;
    if (department && emp.employeeDepartmentName !== department) continue;
    if (team && emp.employeeTeam !== team) continue;
    const period = Array.isArray(req.requestPeriod) ? req.requestPeriod : [];
    const days = period.filter((p) => {
      const t = dayjsTZ(p).valueOf();
      return t >= startTs && t <= endTs;
    }).length;
    if (!days) continue;
    const id = emp.employeeId || req.userSub;
    if (!map.has(id)) {
      map.set(id, { name: empName(emp, req), value: 0 });
    }
    map.get(id).value += days;
  }
  return [...map.values()]
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

export function totalLeaveDays(leaveByTypeRows) {
  return (leaveByTypeRows || []).reduce((s, r) => s + (r.value || 0), 0);
}

/**
 * Full dashboard payload from enriched clockings + leave requests.
 */
export function buildAnalyticsDashboard({
  clockings,
  requests,
  shifts,
  employees,
  filters,
  startTs,
  endTs,
  grain = "month",
}) {
  const activeOnly = filters.employeeScope !== "all";
  const filtered = filterClockings(clockings, { ...filters, activeOnly });
  const leaveFilters = {
    startTs,
    endTs,
    employeeId: filters.employeeId,
    department: filters.department,
    team: filters.team,
    employees,
    activeOnly,
  };
  const leaveTypes = leaveByType(requests, leaveFilters);
  const lateLeaders = latenessLeaders(filtered, shifts);
  const lateMinutes = totalLateMinutes(filtered, shifts);

  return {
    kpis: buildKpis(filtered, totalLeaveDays(leaveTypes), lateMinutes),
    hoursOverTime: hoursOverTime(filtered, { grain }),
    hoursMix: hoursMix(filtered),
    byDepartment: hoursByDepartment(filtered).slice(0, 15),
    byTeam: hoursByTeam(filtered).slice(0, 15),
    topHours: topByHours(filtered),
    topOt: topByOvertime(filtered),
    lateLeaders,
    leaveByType: leaveTypes,
    leaveTopUsers: leaveTopUsers(requests, leaveFilters),
  };
}
